import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronDown, Filter, Search } from "lucide-react";
import { toast } from "react-toastify";
import { imeiAPI, vendorAPI } from "../../services";
import { formatImeiDate, calculatePagination } from "../../utils/imeiHelpers";
import { ImeiTableSkeleton } from "../../components/skeleton";
import "../../style/imei.css";
import EyeIcon from "../../assets/icons/EyeIcon.js";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";
import DeleteModalIcon from "../../assets/delete-modal-icon.svg";
import NoImeiModalIcon from "../../assets/no-imei-modal-icon.svg";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";
import FoundIcon from "../../assets/icons/FoundIcon.js";
import { useSelector } from "react-redux";

/* ---------------- Module-level cache for suppliers ---------------- */
let suppliersCache = null;
let suppliersPromise = null;

/* ---------------- Tooltip Component ---------------- */
const Tooltip = ({ children, content, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.bottom + 8;
      let left =
        triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      // Adjust if tooltip goes off screen
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  if (!show) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ cursor: "help" }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="imei-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 1000,
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};

/* ---------------- utils ---------------- */
function useClickOutside(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onClose]);
}

/* ---------------- toolbar ---------------- */
const ListToolbar = ({
  search,
  setSearch,
  onSearchSubmit,
  suppliers,
  supplierId,
  setSupplierId,
  onLoadSuppliers,
  onSearchSuppliers,
}) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [open, setOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [inputValue, setInputValue] = useState(search);
  const menuRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const supplierSearchInputRef = useRef(null);
  useClickOutside(menuRef, () => setOpen(false));

  // Sync input value when search prop changes
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  // Auto focus supplier search when dropdown opens
  useEffect(() => {
    if (open && supplierSearchInputRef.current) {
      supplierSearchInputRef.current.focus();
    }
  }, [open]);

  // Handle input change without auto search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onSearchSubmit(inputValue);
    }
  };

  return (
    <div className="imei-toolbar">
      {/* Search box */}
      <div
        className={`${isPartsURL
          ? "search-box-parts-imei"
          : isAccessoriesURL
            ? "search-box-acc-imei"
            : "search-box-imei"
          }`}
      >
        <span className="icon" aria-hidden>
          <Search size={16} color="#8C959F" />
        </span>
        <input
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Search by IMEI or serial number..."
          aria-label="Search by name or email"
        />
      </div>

      {/* Supplier dropdown filter */}
      <div className={`supplier-filter ${open ? "open" : ""}`} ref={menuRef}>
        <button
          type="button"
          className={` ${isPartsURL
            ? "supplier-trigger-parts"
            : isAccessoriesURL
              ? "supplier-trigger-acc"
              : "supplier-trigger"
            }`}
          onClick={() => {
            if (!open) onLoadSuppliers();
            setOpen((v) => !v);
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Filter
            size={16}
            className="supplier-filter-icon"
            color="#57606A"
            aria-hidden
          />
          <span>
            {supplierId
              ? suppliers.find((s) => String(s.id) === String(supplierId))
                ?.name || "Supplier"
              : "All Suppliers"}
          </span>
          <ChevronDown
            size={16}
            className="chevron"
            color="#424A53"
            aria-hidden
          />
        </button>

        {open && (
          <div
            className={`${isPartsURL
              ? "supplier-menu-parts"
              : isAccessoriesURL
                ? "supplier-menu-acc"
                : "supplier-menu"
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* filter Supplier search container */}
            <div className="supplier-search-container">
              <div
                className={`${isPartsURL
                  ? "supplier-search-box-parts"
                  : isAccessoriesURL
                    ? "supplier-search-box-acc"
                    : "supplier-search-box"
                  }`}
              >

                <Search size={16} color="#8C959F" />
                <input
                  ref={supplierSearchInputRef}
                  type="text"
                  placeholder="Search Supplier"
                  value={supplierSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSupplierSearch(value);
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    debounceTimerRef.current = setTimeout(() => {
                      onSearchSuppliers(value);
                    }, 300);
                  }}
                  className="supplier-search-input"
                />
              </div>
            </div>
            <ul role="listbox" tabIndex={-1}>
              <li>
                <button
                  role="option"
                  aria-selected={!supplierId}
                  className={!supplierId ? "active" : ""}
                  onClick={() => {
                    setSupplierId("");
                    setOpen(false);
                    setSupplierSearch("");
                  }}
                >
                  All Suppliers
                </button>
              </li>
              {suppliers.map((s) => (
                  <li key={s.id}>
                    <button
                      role="option"
                      aria-selected={String(s.id) === String(supplierId)}
                      className={
                        String(s.id) === String(supplierId) ? "active" : ""
                      }
                      onClick={() => {
                        setSupplierId(s.id);
                        setOpen(false);
                        setSupplierSearch("");
                      }}
                    >
                      {s.name}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- table ---------------- */
const ListTable = ({ rows, onView, onDelete, onImport, loading }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Ensure rows is always an array
  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div className="imei-table-card">
      <table className="imei-table">
        <thead>
          <tr>
            <th style={{ width: 130 }}>RECORD NO.</th>
            <th className="" style={{ width: 160 }}>
              DATE RECORDED
            </th>
            <th>SUPPLIER</th>
            <th>NOTES</th>
            <th className="" style={{ width: 110 }}>
              NUMBERS
            </th>
            <th style={{ width: 120, textAlign: "center" }}>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <ImeiTableSkeleton rows={5} />
          ) : safeRows.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-imei-message">
                <div
                  className=""
                  style={{
                    marginBottom: "4rem",
                    marginTop: "4rem",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <NoDataIcon />
                  <h3
                    style={{
                      marginTop: "1rem",
                      color: "#6E7781",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    No IMEI records found
                  </h3>
                </div>
              </td>
            </tr>
          ) : (
            safeRows.map((r) => (
              <tr key={r.id}>
                <td className="muted">{r.id || "-"}</td>
                <td className="muted">
                  {r.created_at ? formatImeiDate(r.created_at) : "-"}
                </td>
                <td>{r.supplier_name || "-"}</td>
                <td className="imei-muted">
                  <Tooltip
                    content={r.notes || "-"}
                    show={r.notes && r.notes.length > 20}
                  >
                    <span className="truncate">
                      {r.notes && r.notes.length > 20
                        ? `${r.notes.substring(0, 20)}...`
                        : r.notes || "-"}
                    </span>
                  </Tooltip>
                </td>
                <td className="">
                  {typeof r.total_numbers === "number"
                    ? r.total_numbers
                    : Number(r.total_numbers || 0)}
                </td>
                <td className="action-buttons">
                  <button
                    title="View"
                    className={`${isPartsURL
                      ? "action-btn-parts view"
                      : isAccessoriesURL
                        ? "action-btn-acc view"
                        : "action-btn view"
                      }`}
                    onClick={() => onView(r)}
                  >
                    <EyeIcon
                      fill={
                        isPartsURL
                          ? "var(--primary-color-parts)"
                          : isAccessoriesURL
                            ? "var(--primary-color-acc)"
                            : "var(--primary-color-mobile)"
                      }
                    />
                  </button>

                  {/*  Import (Upload) icon instead of download */}
                  {/* <button
                    title="Import IMEIs"
                    className="import"
                    onClick={() => onImport(r)}
                  >
                    <UploadIcon />
                  </button> */}

                  <button
                    title="Delete"
                    className="action-btn delete"
                    onClick={() => onDelete(r)}
                  >
                    <RedDeleteIcon />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ---------------- Add Record Modal ---------------- */
const AddRecordModal = ({ open, onClose, onSubmit, suppliers, onLoadSuppliers, onSearchSuppliers }) => {
  const [supplierId, setSupplierId] = useState("");
  const [numbers, setNumbers] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const supplierMenuRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const supplierSearchInputRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  useClickOutside(supplierMenuRef, () => setSupplierDropdownOpen(false));

  useEffect(() => {
    if (open) {
      setSupplierId("");
      setNumbers("");
      setNotes("");
      setIsSubmitting(false);
      setSupplierDropdownOpen(false);
      setSupplierSearch("");
    }
  }, [open]);

  // Auto focus supplier search when dropdown opens
  useEffect(() => {
    if (supplierDropdownOpen && supplierSearchInputRef.current) {
      supplierSearchInputRef.current.focus();
    }
  }, [supplierDropdownOpen]);

  if (!open) return null;

  return (
    <div
      className="imei-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="imei-modal add-record-modal no-scrollbar"
        role="dialog"
        aria-modal="true"
        aria-labelledby="addRecordTitle"
      >
        <div className="modal-header">
          <h3 id="addRecordTitle">Add New Record</h3>
          <p>Create a new record for new IMEI or Serial numbers.</p>
        </div>
        <div className="modal-body">
          {/* Supplier Dropdown modal */}
          <div className="form-group">
            <label htmlFor="addRecordSupplier">Supplier *</label>
            <div
              className={`supplier-dropdown ${supplierDropdownOpen ? "open" : ""
                }`}
              ref={supplierMenuRef}
            >
              <button
                type="button"
                className={`${isPartsURL ? "supplier-dropdown-trigger-parts" : isAccessoriesURL ? "supplier-dropdown-trigger-acc" : "supplier-dropdown-trigger"}`}
                onClick={() => {
                  if (!supplierDropdownOpen) onLoadSuppliers();
                  setSupplierDropdownOpen((v) => !v);
                }}
                aria-haspopup="listbox"
                aria-expanded={supplierDropdownOpen}
              >
                <span>
                  {supplierId
                    ? suppliers.find((s) => String(s.id) === String(supplierId))
                      ?.name || "Select supplier"
                    : "Select supplier"}
                </span>
                <ChevronDown
                  size={16}
                  className="chevron"
                  color="#424A53"
                  aria-hidden
                />
              </button>

              {supplierDropdownOpen && (
                <div
                  className="supplier-dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Supplier Dropdown search container modal */}
                  <div className="supplier-search-container">
                    <div className={`${isPartsURL ? "supplier-search-box-dropdown-parts" : isAccessoriesURL ? "supplier-search-box-dropdown-acc" : "supplier-search-box-dropdown"}`}>
                      <Search size={16} color="#8C959F" />
                      <input
                        ref={supplierSearchInputRef}
                        type="text"
                        placeholder="Search Supplier"
                        value={supplierSearch}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSupplierSearch(value);
                          if (debounceTimerRef.current) {
                            clearTimeout(debounceTimerRef.current);
                          }
                          debounceTimerRef.current = setTimeout(() => {
                            onSearchSuppliers(value);
                          }, 300);
                        }}
                        className="supplier-search-input"
                        style={{ boxShadow: "none" }}
                      />
                    </div>
                  </div>
                  <ul role="listbox" tabIndex={-1}>
                    {suppliers.map((s) => (
                        <li key={s.id}>
                          <button
                            role="option"
                            aria-selected={String(s.id) === String(supplierId)}
                            className={
                              String(s.id) === String(supplierId)
                                ? "active"
                                : ""
                            }
                            onClick={() => {
                              setSupplierId(s.id);
                              setSupplierDropdownOpen(false);
                              setSupplierSearch("");
                            }}
                          >
                            {s.name}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="addRecordNumbers">Numbers</label>
            <input
              id="addRecordNumbers"
              type="number"
              className={`${isPartsURL ? "numbers-input-imei-parts" : isAccessoriesURL ? "numbers-input-imei-acc" : "numbers-input-imei"}`}
              min="0"
              value={numbers}
              onChange={(e) => setNumbers(e.target.value)}
              placeholder="Enter Number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="addRecordNotes">Notes</label>
            <textarea
              id="addRecordNotes"
              value={notes}
              className={`${isPartsURL ? "notes-textarea-imei-parts" : isAccessoriesURL ? "notes-textarea-imei-acc" : "notes-textarea-imei"}`}
              style={{ minHeight: "100px" }}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter your notes"
            />
          </div>
        </div>

        <div className="modal-actions-imei">
          <button type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className={`${isPartsURL ? "primary-parts" : isAccessoriesURL ? "primary-acc" : "primary"}`}
            disabled={isSubmitting || !supplierId}
            onClick={async () => {
              if (!supplierId) {
                toast.error("Supplier is required");
                return;
              }
              setIsSubmitting(true);
              const capacity = parseInt(numbers, 10);
              const sanitized =
                Number.isFinite(capacity) && capacity > 0 ? capacity : 0;
              try {
                await onSubmit({
                  supplier_id: supplierId,
                  numbers: sanitized,
                  notes: notes.trim(),
                });
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? "Saving..." : "Add Record"}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddRecordModal };

/* ---------------- No Data Found Modal ---------------- */
const NoDataFoundModal = ({ open, onClose, searchTerm }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  if (!open) return null;

  return (
    <div
      className="imei-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteRecordTitle"
        style={{ padding: "24px", maxWidth: "451px" }}
      >
        <div className="modal-icon">
          <img src={NoImeiModalIcon} alt="Delete" />
        </div>
        <h3 className="modal-title" id="deleteRecordTitle">
          IMEI / Serial Not Found
        </h3>
        <p className="modal-message">
          we couldn't find the scanned IMEI/serial in the system. <br />
          Please check the code and try again.
        </p>
        <div className="modal-actions">
          <button type="button" className={`modal-ok-btn${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`} onClick={onClose}>
            Ok
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Data Found Modal ---------------- */
const DataFoundModal = ({ open, onClose, searchResult }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  if (!open || !searchResult) return null;

  return (
    <div
      className="imei-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="data-found-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dataFoundTitle"
      >
        <div className="data-found-modal-icon">
          <FoundIcon />
        </div>

        {/*  Heading  */}
        <h3 className="modal-title" style={{ textAlign: "start", margin: "16px 0 16px 0" }}>
          IMEI Record Found
        </h3>

        {/* Record Details */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <div
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              <span>Record ID: </span>
              {/* <span>IMEI : </span> */}
              <span>{searchResult.id}</span>
            </div>
            <div
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              <span>Date of Registered: </span>
              <span>
                {searchResult.created_at
                  ? formatImeiDate(searchResult.created_at)
                  : "-"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <span
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              Supplier :{" "}
            </span>
            <span
              style={{ fontWeight: "400", fontSize: "14px", color: "#424A53" }}
            >
              {" "}
              {searchResult.supplier_name || "-"}
            </span>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className={`modal-ok-btn${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`} onClick={onClose}>
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Delete Modal ---------------- */
const DeleteRecordModal = ({ open, onClose, onConfirm, record }) => {
  if (!open || !record) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteRecordTitle"
        style={{ padding: "24px", maxWidth: "451px" }}
      >
        <div className="modal-icon">
          <img src={DeleteModalIcon} alt="Delete" />
        </div>
        <h3 className="modal-title" id="deleteRecordTitle">
          IMEI / Serial Not Found
        </h3>
        <p className="modal-message">
          Are you sure you want to delete record{" "}
          <b>{String(record.id).padStart(2, "0")}</b>?<br />
          This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-delete-btn"
            onClick={() => onConfirm(record)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function IMEIList() {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [search, setSearch] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [noDataModalOpen, setNoDataModalOpen] = useState(false);
  const [dataFoundModalOpen, setDataFoundModalOpen] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const initialLoadDone = useRef(false);
  const suppliersLoaded = useRef(false);

  const handleSearchSubmit = (searchValue) => {
    const trimmedValue = searchValue.trim();
    setSearch(trimmedValue);
    setPage(1);
  };

  const searchSuppliers = async (searchTerm) => {
    try {
      const response = await vendorAPI.getVendorsList(searchTerm, 1, 1000);
      setSuppliers(response.results || []);
    } catch (error) {
      console.error("Error searching suppliers:", error);
    }
  };

  // Lazy load suppliers only when needed
  const loadSuppliers = async () => {
    if (suppliersLoaded.current) return;
    suppliersLoaded.current = true;

    // If we already have cached suppliers, use them
    if (suppliersCache) {
      setSuppliers(suppliersCache);
      return;
    }

    // If there's already a fetch in progress, wait for it
    if (suppliersPromise) {
      try {
        const results = await suppliersPromise;
        setSuppliers(results);
      } catch (error) {
        // Error already handled
      }
      return;
    }

    // Start a new fetch
    suppliersPromise = (async () => {
      try {
        const response = await vendorAPI.getVendorsList("", 1, 1000);
        const results = response.results || [];
        suppliersCache = results;
        return results;
      } catch (error) {
        console.error("Error loading suppliers:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load suppliers";
        toast.error(errorMessage);
        suppliersPromise = null;
        suppliersLoaded.current = false;
        throw error;
      }
    })();

    try {
      const results = await suppliersPromise;
      setSuppliers(results);
    } catch (error) {
      // Error already logged
    }
  };

  // Load IMEI records
  useEffect(() => {
    let cancelled = false;
    const loadRecords = async () => {
      // Mark that initial load has been done
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
      }

      setLoading(true);
      try {
        // Always call API, even with empty search to get all records
        const res = await imeiAPI.getImeiList(
          search,
          page,
          pageSize,
          supplierId
        );
        if (!cancelled) {
          // Handle the API response structure - direct response with count, next, previous, results
          // Ensure we properly extract results and count from the response
          const results = Array.isArray(res?.results)
            ? res.results
            : res?.results
              ? [res.results]
              : [];
          const count = res?.count ?? (results.length > 0 ? results.length : 0);

          setData({
            results: results,
            count: count,
          });

          // Handle search results
          if (search) {
            if (results.length > 0 && count > 0) {
              // Data found - show DataFoundModal with first result and clear search
              setSearchResult(results[0]);
              setDataFoundModalOpen(true);
              setSearch("");
            } else {
              // No data found - clear search and show NoDataFoundModal
              setSearch("");
              setNoDataModalOpen(true);
            }
          }
        }
      } catch (error) {
        console.error("Error loading IMEI records:", error);
        if (!cancelled) {
          setData({ results: [], count: 0 });
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load IMEI records";
          toast.error(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadRecords();
    return () => {
      cancelled = true;
    };
  }, [search, supplierId, page]);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await imeiAPI.getImeiList(search, page, pageSize, supplierId);
      // Handle the API response structure - direct response with count, next, previous, results
      // Ensure we properly extract results and count from the response
      const results = Array.isArray(res?.results)
        ? res.results
        : res?.results
          ? [res.results]
          : [];
      const count = res?.count ?? (results.length > 0 ? results.length : 0);

      setData({
        results: results,
        count: count,
      });
    } catch (error) {
      console.error("Error refreshing IMEI records:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to refresh IMEI records";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onImportRow = async (row) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const result = await imeiAPI.importBulkImei(row.id, file);
        toast.success(
          `Imported ${result.added} IMEIs (${result.duplicate} duplicates, ${result.invalid} invalid).`
        );
        await refresh();
      } catch (error) {
        console.error("Error importing IMEIs:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to import IMEIs";
        toast.error(errorMessage);
      }
    };
    input.click();
  };

  const onDeleteRow = (row) => {
    setToDelete(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async (row) => {
    try {
      await imeiAPI.deleteImeiRecord(row.id);
      toast.success(
        `Record ${String(row.id).padStart(2, "0")} deleted successfully`
      );
      setDeleteOpen(false);
      setToDelete(null);
      await refresh();
    } catch (error) {
      console.error("Error deleting record:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete record";
      toast.error(errorMessage);
    }
  };

  const createRecord = async (payload) => {
    try {
      await imeiAPI.createImeiRecord(payload);
      toast.success("Record created successfully");
      setAddOpen(false);
      await refresh();
    } catch (error) {
      console.error("Error creating record:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create record";
      toast.error(errorMessage);
    }
  };

  const paginationInfo = useMemo(
    () => calculatePagination(data.count || 0, page, pageSize),
    [data.count, page, pageSize]
  );

  useEffect(() => {
    // Skip page reset on initial load to prevent double API call
    if (!initialLoadDone.current) {
      return;
    }
    setPage(1);
  }, [search, supplierId]);

  const totalPages = Math.max(1, paginationInfo.totalPages || 1);

  return (
    <div className="imei-page">
      <div className="card">
        <div className="imei-card-top">
          <div className="imei-card-title">
            <h2>IMEI / Serial Bank</h2>
            <p>Manage IMEI / Serial bank</p>
          </div>
          <button
            type="button"
            className={` ${isPartsURL
              ? "create-btn-imei-parts"
              : isAccessoriesURL
                ? "create-btn-imei-acc"
                : "create-btn-imei"
              }`}
            onClick={() => setAddOpen(true)}
          >
            <Plus size={18} />
            Create Record
          </button>
        </div>

        <ListToolbar
          search={search}
          setSearch={setSearch}
          onSearchSubmit={handleSearchSubmit}
          suppliers={suppliers}
          supplierId={supplierId}
          setSupplierId={setSupplierId}
          onLoadSuppliers={loadSuppliers}
          onSearchSuppliers={searchSuppliers}
        />

        <ListTable
          rows={data.results}
          onView={(r) => navigate(`/imei/${r.id}`)}
          onDelete={onDeleteRow}
          onImport={onImportRow}
          loading={loading}
        />

        {data.count > pageSize && (
          <div className="pagination">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="pager">
              <button
                type="button"
                disabled={!paginationInfo.hasPrev}
                className="previous"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={`next${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}
                disabled={!paginationInfo.hasNext}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <AddRecordModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={createRecord}
        suppliers={suppliers}
        onLoadSuppliers={loadSuppliers}
        onSearchSuppliers={searchSuppliers}
      />

      <DeleteRecordModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setToDelete(null);
        }}
        onConfirm={confirmDelete}
        record={toDelete}
      />

      <NoDataFoundModal
        open={noDataModalOpen}
        onClose={() => setNoDataModalOpen(false)}
        searchTerm={search}
      />

      <DataFoundModal
        open={dataFoundModalOpen}
        onClose={() => setDataFoundModalOpen(false)}
        searchResult={searchResult}
      />
    </div>
  );
}
